import { forwardRef, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $themeStore, $profileStore, $profileFeatureStore, $menuStore, $initialAccountsStore } from '@lib/store';
import { useRouteHook, useTabs } from '@shared/hooks';
import { AuthUtils, cn, PaletteUtils, RoutePaths, RouteUtils } from '@shared/utils';
import { Button, Text, Icon, Switcher } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import MoonIcon from '@assets/icons/moon.svg';
import LogoutIcon from '@assets/icons/logout.svg';
import UserIcon from '@assets/icons/user.svg';

export const Sidebar = forwardRef<HTMLDivElement>((_, ref) => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const profileStore = useUnit($profileStore.store);
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const isCollapsedMenu = useUnit($menuStore.store);
  const isDarkTheme = useUnit($themeStore.store);
  const accountStore = useUnit($initialAccountsStore.store);

  const { t } = useTranslation();

  const tabsState = useTabs();
  useEffect(() => {
    if (tabsState.loading) return;
    const isOnPaymentsRoute = location.pathname.startsWith(RoutePaths.payments.onNavigate());
    if (isOnPaymentsRoute && !tabsState.hasPayment) navigate(RoutePaths.dashboard.onNavigate());
  }, [location.pathname, tabsState.loading, tabsState.hasPayment]);

  const allowedMenuItems = useMemo(
    () => RouteUtils.getAllowedMenuItems({ profile: profileStore.data, features: profileFeatureStore.data, accounts: accountStore.data }),
    [profileStore.data, profileFeatureStore.data, accountStore.data],
  );
  return (
    <nav
      ref={ref}
      className={cn(
        'flex h-screen w-auto flex-col border-r border-r-gray-black dark:border-r-gray-wet-asphalt dark:bg-soft-black',
        PaletteUtils.isVcfcPalette ? 'bg-primary' : 'bg-white',
      )}
    >
      <div className='h-[70px] max-h-[70px] min-h-[70px]'>
        <div className={cn('flex h-full items-center justify-between py-4', isCollapsedMenu ? 'px-6' : 'px-8')}>
          <NavLink className='flex h-full items-center justify-center' to={RoutePaths.dashboard.onNavigate()}>
            {PaletteUtils.getLogo(isDarkTheme, isCollapsedMenu)}
          </NavLink>
          {!isCollapsedMenu && (
            <Button
              color='primary'
              className={cn(
                'h-8 w-8 rounded-sm !p-0',
                PaletteUtils.isVcfcPalette && ['!rounded-3xl border border-ice dark:bg-obsidian-shade hover:dark:bg-obsidian-shade', 'max-md:hidden'],
              )}
              onClick={() => $menuStore.events.setIsCollapsed(!isCollapsedMenu)}
            >
              <Icon icon={<ChevronIcon />} color={PaletteUtils.getColor('secondary', 'white')} />
            </Button>
          )}
        </div>
      </div>
      <div
        className={cn(
          'no-scrollbar flex flex-col overflow-auto border-t border-t-gray-black py-6 dark:border-t-gray-wet-asphalt',
          PaletteUtils.isVcfcPalette && 'border-b border-b-gray-black dark:border-b-gray-wet-asphalt',
        )}
      >
        {allowedMenuItems.map((item, idx) => {
          const isActive = location.pathname === item.link || location.pathname.startsWith(item.link + '/');

          return (
            <NavLink
              key={idx}
              to={item.link}
              className={cn(
                'group mb-4 flex w-full cursor-pointer px-8 py-3 transition-colors duration-200',
                PaletteUtils.isInsigniaPalette && 'hover:border-r-primary hover:bg-yellow-brown',
                PaletteUtils.isVcfcPalette && 'hover:bg-primary-transparent hover:dark:bg-slate',
                PaletteUtils.isPersonalPalette && 'hover:bg-primary-600',
                isActive && [
                  'border-r-2',
                  PaletteUtils.isInsigniaPalette && 'border-r-primary bg-yellow-brown',
                  PaletteUtils.isVcfcPalette && 'border-r-white bg-primary-transparent dark:border-r-white/50 dark:bg-slate',
                  PaletteUtils.isPersonalPalette && 'bg-primary-600',
                ],
              )}
            >
              <Icon
                className={cn(
                  'transition-all duration-200',
                  PaletteUtils.isInsigniaPalette && ['group-hover:!text-primary', isActive && '!text-primary'],
                  PaletteUtils.isVcfcPalette && ['text-white opacity-50', 'group-hover:opacity-100', isActive && 'text-white !opacity-100'],
                )}
                icon={item.Icon}
              />
              {!isCollapsedMenu && (
                <Text
                  className={cn('my-auto ml-3', PaletteUtils.isVcfcPalette && ['group-hover:opacity-100', !isActive && 'opacity-50'])}
                  color={PaletteUtils.isVcfcPalette ? 'white' : undefined}
                >
                  {t(item.name)}
                </Text>
              )}
            </NavLink>
          );
        })}
      </div>
      <div className={cn('mt-auto flex flex-col', PaletteUtils.isInsigniaPalette && 'border-t border-t-gray-black dark:border-t-gray-wet-asphalt')}>
        <div className='flex flex-col py-6'>
          <div
            className={cn(
              'group flex w-full cursor-pointer px-8 py-3 transition-colors duration-200',
              PaletteUtils.isInsigniaPalette && 'hover:border-r-primary hover:bg-yellow-brown',
              PaletteUtils.isVcfcPalette && 'hover:bg-primary-transparent hover:dark:bg-slate',
              PaletteUtils.isPersonalPalette && 'hover:bg-primary-600',
            )}
            onClick={() => AuthUtils.logout()}
          >
            <div className='flex items-center justify-center'>
              <Icon
                icon={
                  <LogoutIcon
                    className={cn(
                      'transition-all duration-200',
                      PaletteUtils.isInsigniaPalette && 'group-hover:!text-primary',
                      PaletteUtils.isVcfcPalette && ['text-white opacity-50', 'group-hover:opacity-100'],
                    )}
                  />
                }
              />
            </div>
            {!isCollapsedMenu && (
              <Text
                className={cn('my-auto ml-3 whitespace-nowrap', PaletteUtils.isVcfcPalette && 'group-hover:opacity-100')}
                color={PaletteUtils.isVcfcPalette ? 'white-gray' : 'secondary'}
              >
                {t('buttons.logout')}
              </Text>
            )}
          </div>
          <div
            className={cn(
              'group flex w-full px-8 py-3 transition-colors duration-200',
              PaletteUtils.isInsigniaPalette && 'hover:border-r-primary hover:bg-yellow-brown',
              PaletteUtils.isVcfcPalette && 'hover:bg-primary-transparent hover:dark:bg-slate',
              PaletteUtils.isPersonalPalette && 'hover:bg-primary-600',
              isCollapsedMenu && 'cursor-pointer',
            )}
            onClick={() => $themeStore.events.toggleDarkMode()}
          >
            <div className='flex items-center justify-center'>
              <Icon
                icon={
                  <MoonIcon
                    className={cn(
                      'transition-all duration-200',
                      PaletteUtils.isVcfcPalette && 'text-white opacity-50',
                      PaletteUtils.isInsigniaPalette && 'group-hover:!text-primary',
                      PaletteUtils.isVcfcPalette && ['text-white opacity-50', 'group-hover:opacity-100'],
                    )}
                  />
                }
              />
            </div>
            {!isCollapsedMenu && <Switcher label={t('sidebar.darkMode')} value={isDarkTheme} onChange={() => $themeStore.events.toggleDarkMode()} />}
          </div>
        </div>
        <NavLink
          to={profileFeatureStore.data?.profileSettings?.viewProfile ? RoutePaths.profile.onNavigate() : ''}
          className={cn(
            'flex px-6 py-3 transition-colors duration-200',
            PaletteUtils.isInsigniaPalette && ['bg-primary', profileFeatureStore.data?.profileSettings?.viewProfile && 'hover:bg-primary/70'],
            PaletteUtils.isVcfcPalette && ['bg-golden-olive', profileFeatureStore.data?.profileSettings?.viewProfile && 'hover:bg-golden-olive/70'],
          )}
        >
          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-very-pale-blue'>
            <Icon icon={<UserIcon />} color='black' />
          </div>
          {!isCollapsedMenu && (
            <Text className='my-auto ml-3 font-semibold text-white' weight='semibold' color={PaletteUtils.getColor('black', 'white')}>
              {profileStore.data?.userName}
            </Text>
          )}
        </NavLink>
      </div>
    </nav>
  );
});
